import { useEffect, useRef } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { getSession } from '../api/auth'
import { loadMe } from '../api/me'
import { loadSeasonData } from '../seasonData'
import { findTeamId } from '../teams'
import { isAdminEmail } from '../admin'
import { loadLeagues } from '../api/leagues'
import { loadRivals, loadRivalRequests } from '../api/rivals'
import {
  loadPredictions,
  loadBankerGameId,
  loadDoubleUpUsedGameweeks,
  loadCleanSweepUsedGameweeks,
  loadGoalfestUsedGameweeks,
  loadSeasonHistory,
  loadSeasonDetails,
  loadCurrentGameweek,
  loadGameweekPhases,
} from '../storage'
import type { AppliedPhaseTracker } from './useGameweekPhasePoll'
import type { GameweekStateController } from './useGameweekState'
import type { PredictionsController } from './usePredictions'
import type { ChipsController } from './useChips'
import type { LeaguesController } from './useLeagues'
import type { RivalsController } from './useRivals'
import type { SeasonArchiveController } from './useSeasonArchive'
import type { IdentityController } from './useIdentity'

/** The load's own screen state, owned by App because the render gates on it before this hook has
 *  even been reached. */
export interface SessionGate {
  setIsLoading: Dispatch<SetStateAction<boolean>>
  setLoadError: Dispatch<SetStateAction<string | null>>
  setIsSignedIn: Dispatch<SetStateAction<boolean | null>>
}

export interface UseSessionBootstrapOptions {
  /** The gameweek to fall back to when the account has no stored one yet. */
  startingGameweek: number
  appliedPhase: AppliedPhaseTracker
  session: SessionGate
  identity: IdentityController
  gameweek: GameweekStateController
  predictions: PredictionsController
  chips: ChipsController
  season: SeasonArchiveController
  leagues: LeaguesController
  rivals: RivalsController
}

/**
 * The one mount-time load: check the session, then read every slice the app
 * opens with. There is no anonymous/guest mode, so no session short-circuits straight to the
 * login screen without touching any user data.
 *
 * The current gameweek is fetched first and on its own — every per-gameweek read below is keyed
 * by it — and the rest go out in a single parallel batch.
 *
 * Its writes go into hooks that App declares *after* this one, so the setters are read from a ref
 * refreshed on every render rather than captured once. That keeps this a genuine mount-once
 * effect (an empty dependency array that is honestly empty).
 */
export function useSessionBootstrap(options: UseSessionBootstrapOptions): void {
  const latest = useRef(options)
  useEffect(() => {
    latest.current = options
  })

  useEffect(() => {
    let cancelled = false

    async function loadInitialData() {
      const o = latest.current
      try {
        const { data: sessionData } = await getSession()
        if (cancelled) return
        if (!sessionData) {
          o.session.setIsSignedIn(false)
          o.session.setIsLoading(false)
          return
        }
        o.session.setIsSignedIn(true)
        const { gameweek } = await loadCurrentGameweek(o.startingGameweek)
        const [
          predictions,
          banker,
          doubleUps,
          cleanSweeps,
          goalfests,
          history,
          details,
          leagues,
          rivals,
          rivalRequests,
          me,
          phases,
        ] = await Promise.all([
          loadPredictions(gameweek),
          loadBankerGameId(gameweek),
          loadDoubleUpUsedGameweeks(),
          loadCleanSweepUsedGameweeks(),
          loadGoalfestUsedGameweeks(),
          loadSeasonHistory(),
          loadSeasonDetails(),
          loadLeagues(),
          loadRivals(),
          loadRivalRequests(),
          loadMe(),
          loadGameweekPhases(),
          // Fixtures and results are ~200KB and change rarely: fetched once here
          // and held in the season store, exactly where the bundled constant used to sit. A
          // failure propagates to the load error below, with a retry — never an empty season.
          loadSeasonData(),
        ])
        if (cancelled) return

        o.gameweek.setCurrentGameweek(gameweek)
        // Shared gameweek phase: reflect the admin-driven "clock" so a fresh load
        // shows the passed deadlines + revealed results every other user is already seeing.
        o.gameweek.setDeadlinePassedGameweeks(
          phases.filter((p) => p.deadlinePassed).map((p) => p.gameweek)
        )
        o.gameweek.setSettledGameweeks(
          phases.filter((p) => p.resultsRevealed).map((p) => p.gameweek)
        )
        o.gameweek.setResultsRevealed(
          phases.find((p) => p.gameweek === gameweek)?.resultsRevealed ?? false
        )
        o.appliedPhase.record(gameweek, phases)
        o.predictions.setPredictions(predictions)
        o.predictions.setBankerGameId(banker)
        o.chips.setDoubleUpUsedGameweeks(doubleUps)
        o.chips.setCleanSweepUsedGameweeks(cleanSweeps)
        o.chips.setGoalfestUsedGameweeks(goalfests)
        o.season.setSeasonHistory(history)
        o.season.setSeasonDetails(details)
        o.leagues.setLeagues(leagues)
        o.rivals.setRivals(rivals)
        o.rivals.setRivalRequests(rivalRequests)
        o.identity.setMyUserId(me.userId)
        o.identity.setDisplayName(me.displayName)
        o.identity.setAccountEmail(me.email)
        // The server's answer, because it is the only side that can read
        // ADMIN_EMAILS. The fallback is what an older server gets: the client-side check.
        o.identity.setIsAdmin(me.isAdmin ?? isAdminEmail(me.email))
        // A brand-new user has no server-persisted favourite team yet ("none") — keep the
        // cosmetic client default in that case rather than showing an empty state the rest of the
        // (out-of-scope) UI does not yet handle.
        // The server stores canonical team ids; anything it can't resolve is
        // treated as "no favourite team" rather than passed through as a stray string.
        if (me.favouriteTeam) o.identity.setFavouriteTeam(findTeamId(me.favouriteTeam))
        o.identity.setAvatarPreset(me.avatarPreset)
        o.identity.setDataMode(me.dataMode ?? null)
        // The server's answer, because `QUESTS_ENABLED` is an environment variable
        // and the bundle has no environment. An older server sends nothing, which reads as "no
        // quests" — the section is absent rather than rendered against a set we had to guess.
        o.identity.setQuestsEnabled(me.questsEnabled ?? [])
        // Absent on an older server, which reads as "here from the start" — the same safe default
        // as a user who genuinely was.
        o.identity.setFirstPlayableGameweek(me.firstPlayableGameweek ?? null)
      } catch {
        if (!cancelled) {
          o.session.setLoadError(
            'Failed to load your data. Please check your connection and refresh.'
          )
        }
      } finally {
        if (!cancelled) o.session.setIsLoading(false)
      }
    }

    loadInitialData()
    return () => {
      cancelled = true
    }
  }, [])
}
